import React, { useEffect, useRef } from 'react';
import { SearchAreaControlTypes } from '../types';
import { MAX_SEARCH_AREA } from '../constants';

const SearchAreaControl = ({
  handleSearchArea,
  isEnabled,
  isMobile,
  mobileMapUpdateEnabled,
  query,
  selectMobileMapPin,
}) => {
  // Sibling sr-only live region: va-button is a shadow-DOM web component and
  // does not propagate aria-live on the host to the shadow button that holds
  // the visible text, so placing aria-live on <va-button> alone does not
  // announce text changes. We render an empty-on-mount role="status" element
  // and imperatively update its text via useEffect, which produces a real
  // mutation that assistive tech picks up politely. Matches the established
  // imperative sr-only pattern used elsewhere in the map (see RenderMap.jsx
  // and FacilitiesMap.jsx, which update #screenreader-zoom-message and
  // #map-instructions via direct DOM mutation for the same reason).
  //
  // First-mount caveat: SearchAreaControl is conditionally mounted (only after
  // the user pans/zooms the map). Screen readers reliably announce *mutations*
  // of an already-present live region, but often miss text that is present at
  // the moment the live region is inserted. To force the initial announcement
  // we mount the region empty, then write the label inside requestAnimationFrame
  // so the insertion and the text mutation happen in separate ticks.
  const liveRegionRef = useRef(null);
  const hasAnnouncedRef = useRef(false);
  const containerClass = () => {
    const mobileClass = isMobile
      ? 'mapboxgl-ctrl-bottom-center'
      : 'mapboxgl-ctrl-top-center';
    const radiusClass =
      query?.currentRadius > MAX_SEARCH_AREA
        ? 'mapboxgl-zoomed-out'
        : 'mapboxgl-zoomed-in';

    return `${mobileClass} ${radiusClass}`;
  };

  const handleClick = e => {
    if (e) e.preventDefault();

    if (isMobile && mobileMapUpdateEnabled) {
      selectMobileMapPin(null);
    }

    if (isEnabled) {
      handleSearchArea();
    }
  };

  // Single source of truth for the button label so the va-button text and
  // the sr-only live region can never drift. The label only depends on
  // whether the current radius is past the threshold — not on isEnabled —
  // so toggling the button's enabled state will not re-announce the label.
  const label =
    query?.currentRadius > MAX_SEARCH_AREA
      ? 'Zoom in to search'
      : 'Search this area of the map';

  useEffect(() => {
    const region = liveRegionRef.current;
    if (!region) return undefined;

    const writeLabel = () => {
      if (region.textContent !== label) {
        region.textContent = label;
      }
    };

    if (!hasAnnouncedRef.current) {
      hasAnnouncedRef.current = true;
      const hasRaf = typeof requestAnimationFrame === 'function';
      const handle = hasRaf
        ? requestAnimationFrame(writeLabel)
        : setTimeout(writeLabel, 0);
      return () => {
        if (hasRaf && typeof cancelAnimationFrame === 'function') {
          cancelAnimationFrame(handle);
        } else {
          clearTimeout(handle);
        }
      };
    }

    writeLabel();
    return undefined;
  }, [label]);

  return (
    <div id="search-area-control-container" className={containerClass()}>
      <va-button
        data-testid="search-area-control"
        key={`search-area-control-${isEnabled}`}
        id="search-area-control"
        text={label}
        disabled={!isEnabled}
        onClick={handleClick}
        disable-analytics
      />
      <div
        ref={liveRegionRef}
        role="status"
        className="sr-only"
        data-testid="search-area-control-live-region"
      />
    </div>
  );
};

SearchAreaControl.propTypes = SearchAreaControlTypes;

export default SearchAreaControl;
